<template>
    <div class="core-task" @click="$emit('click')" :class="[{
        'core-your-task': task.isCollaborator,
        'new-task': isNew,
        'has-request': commentRequest
    }, 'task-' + task.completeStatus]"
         ref="wrap">
        <div v-if="isNew" class="new-task-badge">NEW</div>
        <div class="task-id">
            Task #<span>{{ task.id }}</span>
            <span v-if="core.colorIndex" class="contract-name" :class="'core-color-' + core.colorIndex">{{core.core.symbol}}</span>
            <i v-if="isEncrypted" class="material-icons encrypted-task">lock</i>
            <span v-if="task.isCollaborator" class="your-task">your task</span>
        </div>
        <div class="task-reward">
            <TokenAmount
                :value="task.reward"
                :symbol="tokenData.symbol"
                :decimals="tokenData.decimals"
            />
        </div>
        <div class="task-watch" :class="{watching: isWatching}">
            <a
                class="material-icons"
                href="javascript: void 0;"
                @click.stop="watch"
                v-tooltip="{
                    content: tooltipText,
                    trigger: 'manual',
                    show: showTooltip,
                    container: $refs.wrap
                 }"
            >visibility</a>
        </div>
        <div v-if="isEncrypted && !decrypted" class="task-title-encrypted">
            Encrypted
        </div>
        <div v-else class="task-title">
            {{ decrypted || task.content.title }}
        </div>
        <div v-if="task.perPeriod" class="task-perperiod">
            completed {{ task.completionCount }}/{{ task.perPeriod }}
        </div>
        <div v-if="failing" class="task-failing error">
            Effort will fail in {{ failing }}
        </div>
        <div v-if="commentRequest" class="task-comment-request" :class="{'can-respond': canRespond}">
            Comments Requested
            <TokenAmount v-if="!core.freelance()" :value="task.commentRequestReward" :decimals="core.core.decimals" :symbol="core.core.symbol"/>
        </div>
        <div v-if="task.commentCount > 0" class="task-comment-count"
             :class="{'new-comments': newComments}">{{ task.commentCount }}
        </div>
    </div>
</template>

<script>
import TokenAmount from "@/components/TokenAmount";
import {getTokenData} from "@/lib/eth/Token";
import TaskTokenMixin from "@/views/happs/core/freelance/TaskTokenMixin";
import {CoreData, TaskData} from "@/views/happs/core/lib/CoreData";
import CryptMixin from "@/views/happs/core/lib/CryptMixin";
import {FastAesCrypt} from "@/views/happs/core/lib/FastAesCrypt";
import WatchingMixin from "@/views/happs/core/WatchingMixin";
import moment from "moment";
import {mapGetters} from "vuex";

export default {
    name: "Task",
    components: {TokenAmount},
    mixins: [WatchingMixin, CryptMixin, TaskTokenMixin],
    props: {
        task: TaskData
    },
    data() {
        return {
            showTooltip: false,
            tokenData: {
                symbol: "",
                decimals: 18
            }
        }
    },
    computed: {
        ...mapGetters('user', ['loggedIn']),
        core() {
            return this.task.core;
        },
        tooltipText() {
            if (this.isWatching) {
                return "Watching task";
            }
            return "No longer watching task";
        },
        isEncrypted() {
            return this.task?.content?.encryption;
        },
        isNew() {
            return this.loggedIn
                && this.core.latestTask !== null
                && this.task?.id > this.core.latestTask
                && (!this.core.viewedTasks || !this.core.viewedTasks.includes(this.task?.id));
        },
        newComments() {
            if (!this.isWatching) {
                return false;
            }
            let task = this.task;
            if (task.commentCount === 0) {
                return false;
            }

            return task.commentCount > (this.core.state?.comments?.[this.task.id]?.read || 0);
        },
        failing() {
            if (this.task?.efforts?.length > 0 && this.task.status !== 'completed') {
                let failingPeriod = this.core.core.currentPeriod - this.core.ext.taskPeriodLimit;

                if (this.core.endingSoon) {
                    for (
                        /**
                         * @type {EffortData}
                         */
                        let effort of this.task.efforts) {

                        if (effort.creationPeriod <= failingPeriod && (effort.status === 'open' || effort.status === 'completed')) {
                            if (this.core.endsInHours < 2) {
                                return this.core.periodEnds.fromNow(true);
                            } else {
                                return this.core.endsInHours + " hours";
                            }
                        }
                    }
                }
            }
            return null;
        },
        commentRequest() {
            return this.task.commentRequestReward !== "0" && this.task.status === "open";
        },
        canRespond() {
            return !this.task.hasResponded && !this.task.hasResponseRewarded && !this.task.isCollaborator && !this.task.isRequester;
        }
    },
    asyncComputed: {
        decrypted() {
            if (this.isEncrypted && this.core.encryptionKey) {
                return this.crypt.decrypt(this.task.content.title, {password: this.core.encryptionKey});
            }
            return null;
        }
    },
    methods: {
        watch() {
            this.toggleWatch();

            this.showTooltip = true;
            setTimeout(() => {
                this.showTooltip = false;
            }, 3000);
        }
    }
}
</script>

<style lang="scss">
@import "~@/assets/css/custom.scss";

.core-task {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    background: rgba($primary, 0.05);
    border: 1px solid transparent;
    border-radius: 2px;
    padding: 18px 12px;
    margin-bottom: 10px;
    cursor: pointer;
    transition: all .2s;
    position: relative;

    &:hover {
        border-color: rgba($primary, 0.15);
        background: rgba($primary, 0.08);
    }

    &.core-your-task {
        border-left-width: 3px;
        border-left-color: $primary;
    }

    &.new-task {
        border-left-width: 3px;
        border-left-color: #b84444;
    }

    .task-id {
        font-size: 13px;
        color: $text-muted;

        span {
            color: $secondary;
        }

        .contract-name {
            margin-left: 5px;
            font-weight: bold;
            &.core-color-1 {
                color: $step1;
            }
            &.core-color-2 {
                color: $step2;
            }
            &.core-color-3 {
                color: $step3;
            }
            &.core-color-4 {
                color: $step4;
            }
        }
    }

    i.encrypted-task {
        margin-left: 5px;
        color: #999;
    }

    span.your-task {
        margin-left: 5px;
        color: rgba($primary, .7);
    }

    .task-reward {
        font-size: 14px;
        flex: 1 0 auto;
        text-align: right;
        color: rgb(177, 104, 12);

        .token-symbol {
            color: $secondary;
        }
    }

    .task-title, .task-title-encrypted {
        width: 100%;
        overflow-wrap: break-word;
    }

    .task-title-encrypted {
        font-style: italic;
        color: $text-muted;
    }

    .task-perperiod {
        flex: 1 0 100%;
        font-size: 14px;
        color: $secondary;
        text-align: right;
    }

    .new-task-badge {
        position: absolute;
        left: 1px;
        top: 0;

        font-weight: 600;
        font-size: 10px;
        padding: 0 3px;
        border-top-left-radius: 2px;
        color: #b84444;
        transition: opacity .3s;
    }

    &:hover .new-task-badge {
        opacity: 1;
    }

    .task-comment-count {
        position: absolute;
        height: 17px;
        right: -2px;
        bottom: -2px;
        background: #999;
        font-weight: bold;
        font-size: 11px;
        padding: 2px 6px 2px;
        border-radius: 10px;
        color: #fff;

        &.new-comments {
            background: $danger;
        }
    }

    .task-watch {
        font-size: 1.1em;
        position: absolute;
        right: 5px;
        top: 0;

        a {
            color: #ccc;
        }

        a:hover {
            text-decoration: none;
            color: #666;
        }

        &.watching {
            a {
                color: rgba($primary, .7);
            }

            a:hover {
                color: $primary;
            }
        }
    }

    .task-failing {
        font-size: 0.9em;
    }

    &.has-request {
        padding-bottom: 32px;
    }

    .task-comment-request {
        color: $secondary;
        font-size: 0.9em;
        position: absolute;
        padding: 4px 12px;
        bottom: 0;
        left: 0;
        right: 0;
        border-bottom-left-radius: 2px;
        border-bottom-right-radius: 2px;

        &.can-respond {
            background: rgba($purple, .6);
            color: #fff;
        }

        .token-amount {
            margin-left: 10px;
            font-weight: 600;
        }
    }
}
</style>
